import { useState, useEffect } from 'react';
import { CreditCard, Check, ArrowRight, Zap, RefreshCw } from 'lucide-react';
import { transactionApi } from '../../repositories/transaction';
import { Skeleton, SkeletonText } from '../../components/ui/skeleton';
import Button from '../../components/ui/Button';
import { buttonVariants } from '../../components/ui/buttonVariants';

const PricingPage = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await transactionApi.getAllStripeProducts();
        if (response.data && response.data.success) {
          setProducts(response.data.data);
        } else {
          setError('Failed to fetch products');
        }
      } catch (err: any) {
        setError('Error fetching products: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) {
    // Plan-card placeholders rather than a centred spinner on a blank page:
    // the grid keeps its shape, so cards fill in instead of the page jumping.
    return (
      <div className="min-h-screen bg-neutral-50" aria-busy="true">
        <div className="max-w-6xl px-4 py-12 mx-auto">
          <Skeleton className="h-8 mx-auto mb-3 w-72" />
          <Skeleton className="w-56 h-4 mx-auto mb-12" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="overflow-hidden border rounded-2xl border-neutral-200">
                <Skeleton className="w-full h-40 rounded-none" />
                <div className="p-6 space-y-3 bg-white">
                  <SkeletonText lines={4} />
                  <Skeleton className="w-full h-10 mt-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl p-4 mx-auto my-12 text-sm border rounded-lg text-danger-700 bg-danger-50 border-danger-200">
        <p>{error}</p>
      </div>
    );
  }

  const oneTimeProducts = products.filter(
    (p) => p.prices?.[0]?.type === 'one_time' || !p.prices?.[0]?.recurring
  );
  const recurringProducts = products.filter(
    (p) => p.prices?.[0]?.recurring
  );

  const ProductCard = ({ product, featured = false }: { product: any; featured?: boolean }) => {
    const price = product.prices?.[0];
    const billingLabel = price?.recurring ? `/${price.recurring.interval}` : 'one-time';

    return (
      <div className={`relative flex flex-col overflow-hidden transition-shadow rounded-2xl hover:shadow-lg ${
        featured
          ? 'border-2 border-primary-700 shadow-md'
          : 'border border-neutral-200'
      }`}>
        {featured && (
          <div className="absolute top-0 right-0 px-3 py-1 text-xs font-semibold text-white bg-primary-700 rounded-bl-xl rounded-tr-2xl">
            MOST POPULAR
          </div>
        )}

        {/* Header */}
        <div className="p-6 text-white bg-primary-800 min-h-[160px] flex flex-col justify-between">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0 pr-3">
              <p className="mb-1 text-xs font-semibold tracking-widest uppercase text-primary-200">
                {price?.recurring ? price.recurring.interval + 'ly' : 'One-time'}
              </p>
              <h3 className="text-xl font-bold leading-tight line-clamp-2">{product.name}</h3>
            </div>
            <div className="p-2 rounded-lg bg-white/20 shrink-0">
              <CreditCard className="w-5 h-5" />
            </div>
          </div>

          <div>
            <div className="flex items-end gap-1">
              <span className="text-4xl font-extrabold">
                ${price ? (price.unit_amount / 100).toFixed(2) : '0.00'}
              </span>
              <span className="mb-1 text-sm text-primary-200">{billingLabel}</span>
            </div>
          </div>

          {product.metadata?.pitch_limit && (
            <div className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-100">
              <Zap className="w-3 h-3" />
              {product.metadata.pitch_limit} pitches included
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex flex-col flex-grow px-6 py-4 bg-white">
          {product.description && !product.description.startsWith('(') && (
            <p className="mb-4 text-sm leading-relaxed text-neutral-600">
              {product.description}
            </p>
          )}

          <ul className="flex-grow space-y-2 mb-4">
            {(!price?.recurring) && (
              <li className="flex items-center gap-2 text-sm text-neutral-600">
                <Check className="w-4 h-4 text-success-600 shrink-0" />
                Credits valid for 6 months
              </li>
            )}
            {product.metadata?.pitch_limit && (
              <li className="flex items-center gap-2 text-sm text-neutral-600">
                <Check className="w-4 h-4 text-success-600 shrink-0" />
                {product.metadata.pitch_limit} AI-powered pitches
              </li>
            )}
          </ul>

          {product.metadata?.payment_link ? (
            <a
              href={product.metadata.payment_link.trim()}
              target="_blank"
              rel="noopener noreferrer"
              className={`${buttonVariants({ variant: featured ? 'primary' : 'secondary' })} w-full`}
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </a>
          ) : (
            <Button disabled className="w-full">
              Not Available
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero */}
      <div className="py-16 bg-primary-800">
        <div className="px-6 mx-auto text-center max-w-3xl">
          <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-4 text-base text-primary-100">
            One-time pitch credits or flexible subscriptions — pick what works for you.
          </p>
        </div>
      </div>

      <div className="px-6 mx-auto max-w-6xl py-14">

        {/* One-time Credits */}
        {oneTimeProducts.length > 0 && (
          <section className="mb-14">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 rounded-lg bg-primary-50">
                <Zap className="w-5 h-5 text-primary-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-black">Pitch Credit Packs</h2>
                <p className="text-sm text-neutral-500">One-time purchase, no subscription required</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {oneTimeProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  featured={product.name === 'Growth Pack'}
                />
              ))}
            </div>
          </section>
        )}

        {/* Subscriptions */}
        {recurringProducts.length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 rounded-lg bg-primary-50">
                <RefreshCw className="w-5 h-5 text-primary-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-black">Subscription Plans</h2>
                <p className="text-sm text-neutral-500">Recurring billing, cancel anytime</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {recurringProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  );
};

export default PricingPage;
